import { BusyIndicator } from "client/Components";
import {
	useEffectSkipFirstRender,
	useSynchronizedRef,
	useUserTimezone
} from "client/Hooks";
import {
	getClientId,
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import {
	ReactElement,
	ReadonlyRefObject,
	useCallback,
	useContext,
	useEffect,
	useState
} from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { getMillisecondsTillTomorrow } from "client_legacy/ndaw/dateFormatting";
import { BirthdayListItem, list } from "client/Api/Workspace/LiveInformation/Birthday/birthdayLiveInformation";
import { LiveTileContent } from "../LiveTileContent";
import { TileContext } from "../TileContext";
import { BirthdayList } from "./BirthdayList";
import { TimeZone } from "client/DateAndTime/timezones";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { delay } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { newGuid } from "client_legacy/ndaw/guid";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type BirthdayLiveTileContentProps = {
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const BirthdayLiveTileContent = (props: BirthdayLiveTileContentProps): ReactElement => {
	const { onOpenTileMenu } = props;
	const [items, setItems] = useState<readonly ObjectWithClientId<BirthdayListItem>[]>([]);
	const [hasMoreItems, setHasMoreItems] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [versionedPageNumber, setVersionedPageNumber] = useState<VersionedValue<number>>(
		() => ({ value: 0, version: newGuid() })
	);
	const userTimezone: TimeZone = useUserTimezone();

	const {
		icon,
		name,
		environmentAppId,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const userTimezoneRef: ReadonlyRefObject<TimeZone> = useSynchronizedRef(userTimezone);

	useEffect(
		(): (() => void) => {
			const { value: pageNumber } = versionedPageNumber;

			if (pageNumber === 0) {
				setItems([]);
			}

			const loadPage = async (pageNumber: number, signal: AbortSignal): Promise<void> => {
				try {
					// We delay the call by a small amount, the main reason is that when the tile is
					// visible on page load, it is rendered prio to waitForUserConfigurationToInitialize
					// firing, which could change the TimeZone resulting in a unnecessary request.
					await delay(10, signal);

					setIsLoading(true);
					setHasMoreItems(false);

					const page: LazyLoadPageResult<BirthdayListItem> = await list(
						new Date(),
						pageNumber,
						userTimezoneRef.current,
						signal
					);

					setItems(
						(x): readonly ObjectWithClientId<BirthdayListItem>[] => [
							...x,
							...page.items
								.map(setClientId)
						]
					);

					setHasMoreItems(page.hasMore);

					setIsLoading(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					if (pageNumber === 0) {
						setErrorMessage(localize("_UnableToRetrieveItems"));
					}
					setHasMoreItems(false);
					setIsLoading(false);
				}
			};

			const cancellation = new AbortController();
			loadPage(pageNumber, cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			userTimezoneRef,
			versionedPageNumber
		]
	);

	const reloadData = useCallback(
		(): void => {
			setVersionedPageNumber({
				value: 0,
				version: newGuid()
			});
		},
		[]
	);

	useEffectSkipFirstRender(
		(): void => {
			hasImplicitDependencyOn(userTimezone);
			reloadData();
		},
		[reloadData, userTimezone]
	);

	useEffect(
		(): (() => void) => {

			let handle: SetTimeoutHandle | undefined;
			const scheduleRefresh = (): void => {
				const msTillTomorrow: number = getMillisecondsTillTomorrow(userTimezone);

				handle = setTimeout(
					(): void => {
						reloadData();
					},
					msTillTomorrow
				);
			};

			scheduleRefresh();

			return (): void => clearTimeout(handle);
		},
		[reloadData, userTimezone]
	);

	const loadMoreHandler = useCallback(
		(): void => {
			setVersionedPageNumber(
				(x): VersionedValue<number> => ({
					value: x.value + 1,
					version: newGuid()
				})
			);
		},
		[]
	);

	const afterWishSendHandler = useCallback(
		(item: ObjectWithClientId<BirthdayListItem>): void => {
			setItems(
				(state) => {
					return state.map(
						x => {
							if (getClientId(x) === getClientId(item)) {
								return {
									...x,
									canMakeWish: false
								};
							}

							return x;
						}
					);
				}
			);
		},
		[]
	);

	return (
		<LiveTileContent
			name={name}
			icon={icon}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			<LiveTileContent.Header icon={icon}>
				<LiveTileContent.Title text={localize("_Birthdays")} />
			</LiveTileContent.Header>

			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					<LiveTileContent.BusyIndicatorContainer>
						{isLoading && (
							<BusyIndicator />
						)}

						<LiveTileContent.Scrollable>
							{errorMessage
								? <LiveTileContent.Message text={errorMessage} />
								: (
									<BirthdayList
										pageNumber={versionedPageNumber.value}
										isLoading={isLoading}
										items={items}
										hasMoreItems={hasMoreItems}
										onLoadMore={loadMoreHandler}
										onAfterWishSent={afterWishSendHandler}
										timezone={userTimezone}
										onListItemClick={addAnalyticsEvent}
									/>
								)
							}

						</LiveTileContent.Scrollable>
					</LiveTileContent.BusyIndicatorContainer>
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>
		</LiveTileContent>
	);
};
BirthdayLiveTileContent.displayName = "BirthdayLiveTileContent";
